import { createFileRoute, Link } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Pre, Automate, H3 } from '../components/docs/prose'
import { ReconciliationDiagram } from '../components/docs/ReconciliationDiagram'

export const Route = createFileRoute('/docs/reconciliation')({ component: Reconciliation })

const SECTIONS = [
  { id: 'two-facts', title: 'Two different facts' },
  { id: 'states', title: 'The four states' },
  { id: 'grace', title: 'The grace window' },
  { id: 'matching', title: 'How matching works' },
  { id: 'unposted', title: 'What unposted means for you' },
]

function Reconciliation() {
  return (
    <DocPage
      path="/docs/reconciliation"
      title="Reconciliation"
      lede={
        <>
          &ldquo;I paid it&rdquo; and &ldquo;the bank took it&rdquo; are
          different facts. Most trackers store only the first, which is why a
          failed autopay can sit unnoticed for a month.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="two-facts" title="Two different facts">
        <P>
          Two columns hold the two facts, and they are never merged.
        </P>
        <DefList
          items={[
            [
              <C>marked_paid_manually</C>,
              <>
                You said you paid it. Recorded with who said so and when. This
                is an assertion — it is not evidence that any money moved.
              </>,
            ],
            [
              <C>reconciled_at</C>,
              <>
                A real transaction was found and linked to this bill. This is
                evidence. Set by the matcher, never by a person.
              </>,
            ],
          ]}
        />
        <P>
          The gap between them is the whole point. A bill that is marked paid
          and never reconciles is the case worth surfacing: a failed autopay, a
          transfer sent from the wrong account, a payment you meant to make and
          did not.
        </P>
      </Section>

      <Section id="states" title="The four states">
        <ReconciliationDiagram />
        <DefList
          items={[
            [<C>none</C>, 'Nobody has claimed this was paid. The ordinary state of an upcoming bill.'],
            [<C>matched</C>, 'A transaction is linked. The money demonstrably moved.'],
            [<C>pending</C>, 'Marked paid, and still inside the grace window. Not a problem yet — banks take a day or two.'],
            [<C>unposted</C>, 'Marked paid, grace window elapsed, still no transaction. The only state that needs you.'],
          ]}
        />
        <P>
          None of these are stored. They are derived from{' '}
          <C>transaction_id</C>, <C>marked_paid_manually</C>, <C>paid_on</C>{' '}
          and <C>reconcile_grace_days</C> every time they are read. There is no
          nightly job whose failure leaves a bill in a state that stopped being
          true.
        </P>
        <P>
          A bill can also arrive directly at <C>matched</C> without ever being{' '}
          <C>pending</C>: if an imported transaction is linked before you say
          anything, the evidence exists and the assertion was never needed.
        </P>
      </Section>

      <Section id="grace" title="The grace window">
        <P>
          <C>reconcile_grace_days</C> is per bill and defaults to three. It is
          how long FolioWorth will believe you without evidence.
        </P>
        <Pre>{`paid_on         2026-08-04
grace_days               3
today           2026-08-06   ->  pending
today           2026-08-08   ->  unposted`}</Pre>
        <P>
          Three days is right for most payees. A pre-authorised utility debit
          that reliably lands the next morning can be set to one, so a failure
          surfaces faster. Something that routinely takes a week — a bill paid
          through a third-party rent service — should be longer, or it will cry
          wolf every month.
        </P>
      </Section>

      <Section id="matching" title="How matching works">
        <P>
          Matching runs over bills that are marked paid, not yet linked, and
          have both a payment date and an amount. A transaction qualifies when
          all of these hold:
        </P>
        <DefList
          items={[
            ['Same household', 'Enforced by row-level security, not just by the query.'],
            ['Same funding account', 'Unless the bill does not name one, in which case any account is allowed.'],
            ['An outflow', 'The transaction has to be money leaving.'],
            ['Amount within one cent', 'Absolute value against amount_paid. Not "roughly" — one cent.'],
            ['Dated from the day before payment to the end of the grace window', 'Wide enough for a weekend, narrow enough not to catch next month.'],
            ['Not already linked to another bill', 'One transaction settles at most one bill.'],
          ]}
        />
        <P>
          If more than one candidate survives, the one closest in date wins,
          and only one is taken.
        </P>
        <H3>Why so strict</H3>
        <P>
          A looser match — same week, roughly the right amount — would
          reconcile the wrong transaction some of the time, and a wrongly
          reconciled bill is worse than an unreconciled one. It tells you
          everything is fine while the real payment is still missing. Leaving
          it <C>unposted</C> for you to look at is the correct failure mode.
        </P>
      </Section>

      <Section id="unposted" title="What unposted means for you">
        <P>
          Work the list. There are only three explanations, and each has a
          different fix.
        </P>
        <DefList
          items={[
            ['The payment failed', 'Autopay bounced, the card on file expired, the account was short. Pay it now; this is what the state exists to catch.'],
            ['It paid from somewhere else', 'You paid the Visa from savings, not chequing. Correct the funding account on the bill and re-run matching.'],
            ['The transaction is not imported yet', 'A manual account, or a connection you have not synced. Sync, then re-run matching.'],
          ]}
        />
      </Section>

      <Automate>
        Matching is one action over the whole household — you do not reconcile
        bills one at a time. Run it after a sync and everything that can be
        matched is, in one pass. Note that it is not yet a background job:
        nothing reconciles until a sync and a match are triggered. See{' '}
        <Link to="/docs/automating" style={{ color: 'var(--accent-text)' }}>Automating it</Link>.
      </Automate>
    </DocPage>
  )
}
